import { useState, useMemo, ReactNode } from 'react';

export interface Column<T> {
	key: keyof T;
	header: string;
	sortable?: boolean;
	render?: (value: any, row: T) => ReactNode;
	className?: string;
}

interface TableProps<T> {
	data: T[];
	columns: Column<T>[];
	onRowClick?: (row: T) => void;
	emptyMessage?: string;
}

function Table<T extends Record<string, any>>({
	data,
	columns,
	onRowClick,
	emptyMessage = 'No data available',
}: TableProps<T>) {
	const [sortConfig, setSortConfig] = useState<{
		key: keyof T;
		direction: 'asc' | 'desc';
	} | null>(null);

	const sortedData = useMemo(() => {
		if (!sortConfig) return data;

		const sorted = [...data].sort((a, b) => {
			let aVal = a[sortConfig.key];
			let bVal = b[sortConfig.key];

			if (typeof aVal === 'string') {
				aVal = aVal.toLowerCase();
				bVal = bVal.toLowerCase();
			}

			if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
			if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
			return 0;
		});

		return sorted;
	}, [data, sortConfig]);

	const handleSort = (key: keyof T) => {
		setSortConfig(prevConfig => {
			if (prevConfig?.key === key) {
				return {
					key,
					direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
				};
			}
			return { key, direction: 'asc' };
		});
	};

	const getSortClass = (key: keyof T) => {
		if (!sortConfig || sortConfig.key !== key) return '';
		return sortConfig.direction;
	};

	if (data.length === 0) {
		return (
			<div className="text-center py-12 text-gray-500 dark:text-gray-400">
				<p>{emptyMessage}</p>
			</div>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse mt-3">
				<thead>
					<tr>
						{columns.map(col => (
							<th
								key={String(col.key)}
								className={`text-left p-3 text-gray-600 dark:text-gray-300 text-sm font-semibold border-b-2 border-indigo-100 dark:border-gray-700 ${
									col.sortable ? `sortable ${getSortClass(col.key)}` : ''
								} ${col.className || ''}`}
								onClick={() => col.sortable && handleSort(col.key)}
							>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{sortedData.map((row, idx) => (
						<tr
							key={idx}
							className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-blue-50/30 dark:hover:bg-gray-700/30' : ''}`}
							onClick={() => onRowClick?.(row)}
						>
							{columns.map(col => (
								<td key={String(col.key)} className={`p-3.5 border-t border-indigo-100 dark:border-gray-700 text-gray-900 dark:text-gray-100 ${col.className || ''}`}>
									{col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '')}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default Table;
